import { AfterViewInit, DestroyRef, Directive, ElementRef, HostListener, OnDestroy, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FavouriteService, OpenLoginDialog } from '@casinocore/platform/core';
import { FavIconKindType } from '@frontend/casino-ui';
import { CookieService, UserService } from '@frontend/vanilla/core';
import { Subscription } from 'rxjs';

const FAV_CLASS = {
    [FavIconKindType.Heart]: {
        OUTLINED_FAV_CLASS: 'theme-heart-outline',
        SOLID_FAV_CLASS: 'theme-heart-solid',
    },
    [FavIconKindType.Star]: {
        OUTLINED_FAV_CLASS: 'theme-rating-star-empty',
        SOLID_FAV_CLASS: 'theme-rating-star-full',
    },
};

@Directive({
    selector: '[csFavoriteConjugate]',
    standalone: true,
})
export class FavouriteConjugateDirective implements AfterViewInit, OnDestroy {
    gameId = input.required<string>();
    gameSection = input.required<string>();
    isFav = input(false);
    shouldTrackSection = input<boolean>();
    csFavoriteConjugateIconKind = input.required<FavIconKindType>();

    currFavState = signal(this.isFav());
    private subscription: Subscription;

    private readonly hostComponent = inject(ElementRef);
    private readonly favouriteService = inject(FavouriteService);
    private readonly cookieService = inject(CookieService);
    private readonly loginDialog = inject(OpenLoginDialog);
    private readonly userService = inject(UserService);
    private destroyRef = inject(DestroyRef);

    constructor() {}

    ngAfterViewInit(): void {
        this.subscription = this.favouriteService.favouriteGamesFeedObservable
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((favouriteGames) => {
                if (favouriteGames && favouriteGames.length > 0) {
                    const isFavorite = this.favouriteService.checkForFavouriteGame({
                        gameId: this.gameId(),
                        fromCasino: true,
                    });
                    this.currFavState.set(isFavorite);
                    this.setFavouriteClass();
                }
            });
    }

    @HostListener('click', ['$event'])
    clickHandler(event: Event) {
        event.stopImmediatePropagation();
        if (this.userService.isAuthenticated) {
            this.favouriteService.toggleGameFavourite({
                gameId: this.gameId(),
                fromCasino: true,
                shouldTrackSection: this.shouldTrackSection(),
                gameSection: this.gameSection(),
            });
            this.currFavState.set(!this.currFavState());
            this.setFavouriteClass();
        } else {
            this.cookieService.putObject('favouriteGame', {
                gameId: this.gameId(),
                fromCasino: true,
                shouldTrackSection: this.shouldTrackSection(),
                gameSection: this.gameSection(),
            });
            const loginResponseObj: any = {
                loginMessageKey: 'Favourite',
            };
            this.loginDialog.open(loginResponseObj);
        }
    }

    private setFavouriteClass() {
        const decoratorClassHost = this.hostComponent?.nativeElement?.querySelector('[data-decoratorclass-host]') as HTMLElement;
        if (this.currFavState()) {
            decoratorClassHost?.classList?.add(FAV_CLASS[this.csFavoriteConjugateIconKind()].SOLID_FAV_CLASS);
            decoratorClassHost?.classList?.remove(FAV_CLASS[this.csFavoriteConjugateIconKind()].OUTLINED_FAV_CLASS);
            return;
        }
        decoratorClassHost?.classList?.add(FAV_CLASS[this.csFavoriteConjugateIconKind()].OUTLINED_FAV_CLASS);
        decoratorClassHost?.classList?.remove(FAV_CLASS[this.csFavoriteConjugateIconKind()].SOLID_FAV_CLASS);
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
